import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LogOut, Trash2, UserPlus } from 'lucide-react';
import type { api, WorkspaceRole } from '@stageplottter/core';
import { api as dto, memberCan } from '@stageplottter/core';
import { useAsync } from '../../../hooks/useAsync';
import { useMe, meStore } from '../../../shell/me';
import { ErrorBox, errorMessageKey } from '../../../pages/ErrorBox';
import { CommitInput } from '../../../ui/CommitInput';
import { toastStore } from '../../../state/toasts';
import { sharingApi } from '../api';
import { TemplatesSection } from '../../templates/TemplatesSection';

const ROLES: readonly WorkspaceRole[] = ['owner', 'admin', 'editor', 'viewer'];

export function formatBytes(n: number, lang: string): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toLocaleString(lang, { maximumFractionDigits: i === 0 ? 0 : 1 })} ${units[i]}`;
}

function useReport() {
  const { t } = useTranslation();
  return (err: unknown) =>
    void toastStore.getState().push({ kind: 'error', message: t(errorMessageKey(err)) });
}

function Usage({ ws }: { ws: api.Workspace }) {
  const { t, i18n } = useTranslation();
  const rows: [string, string, number, number][] = [
    ['boards', `${ws.usage.boards} / ${ws.limits.boards}`, ws.usage.boards, ws.limits.boards],
    [
      'activeLinks',
      `${ws.usage.activeLinks} / ${ws.limits.activeLinks}`,
      ws.usage.activeLinks,
      ws.limits.activeLinks,
    ],
    [
      'storage',
      `${formatBytes(ws.usage.storageBytes, i18n.language)} / ${formatBytes(ws.limits.storageBytes, i18n.language)}`,
      ws.usage.storageBytes,
      ws.limits.storageBytes,
    ],
  ];
  return (
    <section className="settings-section" aria-labelledby="ws-usage">
      <h2 id="ws-usage">{t('sharing.workspace.usage')}</h2>
      <p className="muted small">{t('sharing.workspace.plan', { plan: ws.plan })}</p>
      <dl className="usage-list">
        {rows.map(([key, text, used, limit]) => (
          <div key={key} className="usage-row">
            <dt>{t(`sharing.workspace.${key}`)}</dt>
            <dd>
              <meter
                min={0}
                max={Math.max(limit, 1)}
                value={used}
                high={limit * 0.8}
                aria-label={t(`sharing.workspace.${key}`)}
              />{' '}
              <span>{text}</span>
            </dd>
          </div>
        ))}
        <div className="usage-row">
          <dt>{t('sharing.workspace.guestsPerLink')}</dt>
          <dd>{ws.limits.guestsPerLink}</dd>
        </div>
      </dl>
    </section>
  );
}

function Members({ wsId, myRole, myId }: { wsId: string; myRole: WorkspaceRole; myId: string }) {
  const { t } = useTranslation();
  const report = useReport();
  const sapi = sharingApi();
  const members = useAsync(`members:${wsId}`, () => sapi.listMembers(wsId));
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('editor');
  const [emailError, setEmailError] = useState(false);
  const [confirm, setConfirm] = useState<string | null>(null);
  const manage = memberCan(myRole, 'member.manage');
  const assignable = ROLES.filter((r) => r !== 'owner' || myRole === 'owner');

  const add = () => {
    if (!dto.Email.safeParse(email.trim()).success) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    sapi.addMember(wsId, { email: email.trim(), role }).then(() => {
      setEmail('');
      toastStore.getState().push({ kind: 'success', message: t('sharing.workspace.added') });
      members.reload();
    }, report);
  };

  return (
    <section className="settings-section" aria-labelledby="ws-members">
      <h2 id="ws-members">{t('sharing.workspace.members')}</h2>
      {members.status === 'loading' && <p className="muted">{t('common.loading')}</p>}
      {members.status === 'error' && <ErrorBox error={members.error} onRetry={members.reload} />}
      {members.status === 'ready' && (
        <ul className="member-list">
          {members.data.map((m) => {
            const self = m.userId === myId;
            const canChange = manage && (m.role !== 'owner' || myRole === 'owner');
            return (
              <li key={m.userId} className="member-row">
                <span className="member-name">
                  <strong>{m.name}</strong> <span className="muted small">{m.email}</span>
                  {self && (
                    <>
                      {' '}
                      <span className="badge">{t('sharing.workspace.you')}</span>
                    </>
                  )}
                </span>
                {canChange ? (
                  <select
                    aria-label={t('sharing.workspace.roleOf', { name: m.name })}
                    value={m.role}
                    onChange={(e) =>
                      sapi
                        .updateMember(wsId, m.userId, e.target.value as WorkspaceRole)
                        .then(() => members.reload(), report)
                    }
                  >
                    {ROLES.filter((r) => assignable.includes(r) || r === m.role).map((r) => (
                      <option key={r} value={r}>
                        {t(`role.${r}`)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="badge">{t(`role.${m.role}`)}</span>
                )}
                {(canChange || self) &&
                  (confirm === m.userId ? (
                    <span className="confirm-inline">
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        onClick={() =>
                          sapi.removeMember(wsId, m.userId).then(() => {
                            setConfirm(null);
                            if (self) void meStore.getState().load();
                            members.reload();
                          }, report)
                        }
                      >
                        {self
                          ? t('sharing.workspace.confirmLeave')
                          : t('sharing.workspace.confirmRemove')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-small btn-ghost"
                        onClick={() => setConfirm(null)}
                      >
                        {t('action.cancel')}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="icon-btn danger"
                      aria-label={
                        self
                          ? t('sharing.workspace.leave')
                          : t('sharing.workspace.remove', { name: m.name })
                      }
                      onClick={() => setConfirm(m.userId)}
                    >
                      {/* leaving is no deletion (OIL-237) */}
                      {self ? <LogOut size={15} /> : <Trash2 size={15} />}
                    </button>
                  ))}
              </li>
            );
          })}
        </ul>
      )}
      {manage && (
        <form
          className="inline-form"
          noValidate
          aria-label={t('sharing.workspace.addTitle')}
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <label className="field">
            <span>{t('sharing.workspace.email')}</span>
            <input
              type="email"
              value={email}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'member-email-err' : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {emailError && (
            <span className="field-error" id="member-email-err">
              {t('sharing.guest.error.emailInvalid')}
            </span>
          )}
          <label className="field narrow">
            <span>{t('sharing.workspace.role')}</span>
            <select value={role} onChange={(e) => setRole(e.target.value as WorkspaceRole)}>
              {assignable.map((r) => (
                <option key={r} value={r}>
                  {t(`role.${r}`)}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={email.trim() === ''}>
            <UserPlus size={15} aria-hidden="true" /> {t('sharing.workspace.add')}
          </button>
        </form>
      )}
      {manage && <p className="muted small">{t('sharing.workspace.addHint')}</p>}
    </section>
  );
}

/** `/w/:wsId/settings`: rename, plan usage/limits, members (add by e-mail, roles, remove). */
export function WorkspaceSettingsPage() {
  const { t } = useTranslation();
  const { wsId = '' } = useParams();
  const me = useMe();
  const report = useReport();
  const sapi = sharingApi();
  const ws = useAsync(`workspace:${wsId}`, () => sapi.getWorkspace(wsId));
  return (
    <div className="page">
      <Link to={`/w/${encodeURIComponent(wsId)}`} className="btn btn-ghost">
        <ArrowLeft size={15} aria-hidden="true" /> {t('boards.title')}
      </Link>
      <h1>{t('sharing.workspace.title')}</h1>
      {ws.status === 'loading' && <p className="muted">{t('common.loading')}</p>}
      {ws.status === 'error' && <ErrorBox error={ws.error} onRetry={ws.reload} />}
      {ws.status === 'ready' && (
        <>
          <section className="settings-section" aria-labelledby="ws-general">
            <h2 id="ws-general">{t('sharing.workspace.general')}</h2>
            <label className="field">
              <span>{t('sharing.workspace.name')}</span>
              {memberCan(ws.data.role, 'workspace.manage') ? (
                <CommitInput
                  aria-label={t('sharing.workspace.name')}
                  value={ws.data.name}
                  onCommit={(v) => {
                    const name = v.trim();
                    if (name === '' || name.length > 100) return false;
                    sapi.renameWorkspace(wsId, name).then(() => {
                      toastStore
                        .getState()
                        .push({ kind: 'success', message: t('sharing.workspace.renamed') });
                      ws.reload();
                      void meStore.getState().load();
                    }, report);
                  }}
                />
              ) : (
                <span>{ws.data.name}</span>
              )}
            </label>
            <p className="muted small">
              {t('sharing.workspace.yourRole', { role: t(`role.${ws.data.role}`) })}
            </p>
          </section>
          <Usage ws={ws.data} />
          <Members wsId={wsId} myRole={ws.data.role} myId={me.status === 'ready' ? me.me.id : ''} />
          <TemplatesSection wsId={wsId} canEdit={memberCan(ws.data.role, 'template.edit')} />
        </>
      )}
    </div>
  );
}
